import { useEffect, useState } from 'react';

/**
 * Track the browser's online/offline state via `navigator.onLine` and the
 * `online`/`offline` window events (Phase 5 Stufe I Slice 8 Polish,
 * 2026-05-03). Used by the global OfflineBanner — does NOT switch
 * react-query into an offline cache yet (offline-first PWA is explicit
 * out-of-scope per Stufe-I plan-doc).
 *
 * SSR-safe: defaults to `true` if `navigator` is undefined (e.g. during
 * the initial server render from vite — production-only concern but
 * harmless on the client too).
 */
export function useOnlineStatus(): boolean {
  const [online, setOnline] = useState<boolean>(() =>
    typeof navigator === 'undefined' ? true : navigator.onLine,
  );

  useEffect(() => {
    function handleOnline(): void {
      setOnline(true);
    }
    function handleOffline(): void {
      setOnline(false);
    }
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return online;
}
